import { notification } from "antd";
import { env } from "../env";

// Local safety switch for browsing a real backend (e.g. prod) from a dev machine.
// With REACT_APP_READ_ONLY=true in public/env.js, every request to the backend that
// could change something is blocked. Patched at the XHR/fetch level because the
// SDK services use several axios instances.
const SAFE_METHODS = ["GET", "HEAD", "OPTIONS"];
// Signing in and refreshing the session are writes the app needs to read anything.
const ALLOWED_WRITES = [/\/ms-usermanagement\/users\/(login|login-challenges|refresh-token)(?:[/?]|$)/];

function isBlocked(method: string, url: string): boolean {
    const backend = env.REACT_APP_BACKEND_ENDPOINT;
    return Boolean(backend) &&
        url.startsWith(backend) &&
        !SAFE_METHODS.includes(method.toUpperCase()) &&
        !ALLOWED_WRITES.some(pattern => pattern.test(url));
}

function blocked(method: string, url: string): Error {
    const path = url.replace(env.REACT_APP_BACKEND_ENDPOINT, "");
    notification.warning({
        key: "read-only",
        message: "Read-only mode",
        description: `${method.toUpperCase()} ${path} was not sent. This local app is connected to a real backend in read-only mode.`
    });
    return new Error(`Read-only mode: ${method.toUpperCase()} ${path} blocked`);
}

export function installReadOnlyGuard() {
    if (env.REACT_APP_READ_ONLY !== "true") {
        return;
    }

    const open = XMLHttpRequest.prototype.open;
    const send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest & { __ro?: [string, string] }, method: string, url: string | URL, ...rest: any[]) {
        this.__ro = [method, String(url)];
        return (open as any).call(this, method, url, ...rest);
    };
    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest & { __ro?: [string, string] }, body?: any) {
        const [method, url] = this.__ro || ["GET", ""];
        if (isBlocked(method, url)) {
            throw blocked(method, url);
        }
        return send.call(this, body);
    };

    const originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
        const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
        const method = init?.method || (input instanceof Request ? input.method : "GET");
        if (isBlocked(method, url)) {
            return Promise.reject(blocked(method, url));
        }
        return originalFetch(input, init);
    };

    console.info("[read-only] Writes to", env.REACT_APP_BACKEND_ENDPOINT, "are blocked");
}
